import React, { useMemo } from 'react';

import Mousetrap from 'mousetrap';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { MiniModalLoadingFallbackComponent } from '@atlassian/conversation-assistant';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { MiniModalPropsInternal } from '../common/types/shared-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { getChatRefreshV2DynamicConfig } from '../common/utils/chat-refresh-v2-config';

// TODO: Remove this once we clean up the FG for mini modal static positioning
const styles = cssMap({
	miniModalStyles: {
		position: 'absolute',
		zIndex: 800,
		right: 0,
		// @ts-expect-error - Percentage needed for positioning, not a space token
		bottom: '100%',
	},
});

const LazyAIMiniModal = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-mini-modal" */ './main').then((module) => ({
		default: module.AIMiniModal,
	})),
);

export const preloadAIMiniModal = () => {
	import(/* webpackChunkName: "@atlaskit-internal_ai-mini-modal" */ './main').then((module) => ({
		default: module.AIMiniModal,
	}));
};

export const AIMiniModal = (props: MiniModalPropsInternal): React.JSX.Element => {
	const { isMiniModalStaticPositioningEnabled } = getChatRefreshV2DynamicConfig();

	const mousetrapRef = React.useRef<Mousetrap.MousetrapInstance | null>(null);
	const setContainerRef = React.useCallback((node: any) => {
		mousetrapRef.current?.reset();
		if (!node) {
			return;
		}
		mousetrapRef.current = new Mousetrap(node);
	}, []);

	const suppressKeyPress: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
		mousetrapRef.current?.bind(event.key.toLocaleLowerCase(), (e) => e.stopPropagation());
	};

	const miniModal = useMemo(() => {
		// 48px = nav header height, 8px = object sidebar bottom padding, 16px = AI smart button bottom margin, 8px = extra margin so it doesn't align with nav header
		const defaultTopOffset = 48 + 8 + 16 + 8;

		return (
			<React.Suspense
				fallback={
					<MiniModalLoadingFallbackComponent
						topOffset={props.topOffset || defaultTopOffset}
						isNav4Enabled={props.isNav4Enabled}
					/>
				}
			>
				<LazyAIMiniModal 
					{...props} 
					topOffset={props.topOffset || defaultTopOffset}
				/>
			</React.Suspense>
		);
	}, [props]);

	return (
		<Box
			ref={setContainerRef}
			onKeyDown={suppressKeyPress}
			xcss={cx(isMiniModalStaticPositioningEnabled ? undefined : styles.miniModalStyles)}
			testId="ai-smart-button-mini-modal-wrapper"
		>
			{isMiniModalStaticPositioningEnabled && props.overrideMiniModalWrapper
				? props.overrideMiniModalWrapper(miniModal)
				: miniModal}
		</Box>
	);
};
